/**
 * Public Asset Landing Page.
 *
 * Unauthenticated page displayed when a buyer scans a QR code.
 * Shows asset details, photos, specs, and a financing calculator.
 * Does NOT show financial data (COGS, purchase price).
 */

import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface PublicAsset {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  category: string;
  year: number | null;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  status: string;
  location: string | null;
  images: string[] | null;
  organization_name: string;
}

interface FinancingResult {
  price: number;
  down_payment: number;
  financed_amount: number;
  annual_rate_pct: number;
  term_months: number;
  monthly_payment: number;
  total_paid: number;
  total_interest: number;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function PublicAssetPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: asset,
    isLoading,
    isError,
  } = useQuery<PublicAsset>({
    queryKey: ["public-asset", id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/public/assets/${id}`)).data as PublicAsset,
    enabled: !!id,
  });

  // Financing calculator state.
  const [showCalc, setShowCalc] = useState(false);
  const [price, setPrice] = useState("");
  const [downPayment, setDownPayment] = useState("0");
  const [rate, setRate] = useState("6.5");
  const [term, setTerm] = useState("60");
  const [financing, setFinancing] = useState<FinancingResult | null>(null);

  const calculateFinancing = async () => {
    const resp = await apiClient.get(
      `/api/v1/public/financing/calculate?price=${price}&down_payment=${downPayment}&annual_rate=${rate}&term_months=${term}`,
    );
    setFinancing(resp.data as FinancingResult);
  };

  if (isLoading)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );

  if (isError || !asset) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface-sunken)]">
        <Card className="max-w-md text-center">
          <h2 className="text-xl font-bold text-[var(--text-primary)]">
            Asset Not Available
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            This item may have been sold or is not currently listed.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(135deg, var(--color-bg-gradient-from), var(--color-bg-gradient-via), var(--color-bg-gradient-to))`,
      }}
    >
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <p className="text-xs uppercase tracking-widest text-[var(--text-tertiary)]">
            {asset.organization_name}
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[var(--text-primary)]">
            {asset.name}
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {asset.category} &middot; {asset.sku}
          </p>
        </div>

        {/* Main Info */}
        <Card className="mb-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[
              ["Year", asset.year],
              ["Make", asset.make],
              ["Model", asset.model],
              ["Serial #", asset.serial_number],
              ["Status", asset.status],
              ["Location", asset.location],
            ].map(([label, value]) =>
              value ? (
                <div key={label as string}>
                  <p className="text-xs text-[var(--text-tertiary)]">{label}</p>
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {String(value)}
                  </p>
                </div>
              ) : null,
            )}
          </div>
          {asset.description && (
            <div className="mt-4 border-t border-[var(--color-border)] pt-4">
              <p className="text-sm text-[var(--text-secondary)]">
                {asset.description}
              </p>
            </div>
          )}
        </Card>

        {/* Request Quote / Contact */}
        <Card className="mb-6 text-center">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">
            Interested?
          </h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Contact us for pricing, availability, and financing options.
          </p>
          <div className="mt-4 flex justify-center gap-3">
            <Button
              onClick={() => {
                setShowCalc(!showCalc);
                if (!price) setPrice("0");
              }}
            >
              {showCalc ? "Hide Calculator" : "Payment Calculator"}
            </Button>
          </div>
        </Card>

        {/* Financing Calculator */}
        {showCalc && (
          <Card className="mb-6 animate-fade-in">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-4">
              Estimate Your Payment
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Input
                label="Price ($)"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="45000"
              />
              <Input
                label="Down Payment ($)"
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                placeholder="5000"
              />
              <Input
                label="Rate (%)"
                type="number"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
              <Input
                label="Term (months)"
                type="number"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={calculateFinancing}>
                Calculate
              </Button>
            </div>
            {financing && (
              <div className="mt-4 rounded-[var(--radius-xl)] bg-brand-50 dark:bg-brand-900/20 p-4 text-center animate-fade-in">
                <p className="text-3xl font-bold text-brand-600">
                  {fmt(financing.monthly_payment)}
                  <span className="text-sm font-normal text-[var(--text-secondary)]">
                    /mo
                  </span>
                </p>
                <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                  {financing.term_months} months at {financing.annual_rate_pct}%
                  &middot; Total: {fmt(financing.total_paid)} &middot; Interest:{" "}
                  {fmt(financing.total_interest)}
                </p>
              </div>
            )}
          </Card>
        )}

        <p className="text-center text-xs text-[var(--text-tertiary)]">
          Powered by TradeRoute
        </p>
      </div>
    </div>
  );
}

export default PublicAssetPage;
